import { defineStore, acceptHMRUpdate } from 'pinia';
import { canViewAdoption } from '@/predict/predict.permissions';
import { getAvailableAnalyses, type PredictPendoAnalysis } from '@/stores/api/predict.api';
import { useAuthStore } from '@/stores/auth.store';
import { useSubscriptionStore } from '@/stores/subscription.store';

const inFlightFetches = new Map<string, Promise<PredictPendoAnalysis[]>>();

export interface PredictAccessState {
    analysesBySubId: Record<string, PredictPendoAnalysis[]>;
    failedSubIds: string[];
}

export function getInitialState (): PredictAccessState {
    return {
        analysesBySubId: {},
        failedSubIds: [],
    };
}

function activeSubId (): string | null {
    return useSubscriptionStore().getActiveSub?.id ?? null;
}

export const usePredictAccessStore = defineStore('predictAccess', {
    state: getInitialState,
    getters: {
        // Scoped to the active subscription: the handshake may cache another one via ?pendoSubscriptionId.
        activeAnalyses: (state): PredictPendoAnalysis[] | null => {
            const subId = activeSubId();

            return (subId && state.analysesBySubId[subId]) || null;
        },
        hasLoadError: (state): boolean => {
            const subId = activeSubId();

            return Boolean(subId && state.failedSubIds.includes(subId));
        },
        isLoaded (): boolean {
            return this.activeAnalyses !== null || this.hasLoadError;
        },
        analysisAppIds (): string[] | null {
            return this.activeAnalyses
                ? [...new Set(this.activeAnalyses.map(({ appId }) => String(appId)))]
                : null;
        },
        canViewApp: () => (appId: string): boolean => {
            const auth = useAuthStore();

            return auth.hasAdminAccess || canViewAdoption(appId, auth.accessibleAppIds);
        },
    },
    actions: {
        // Shared by the access check and the Predict handshake so a page load asks once per subscription.
        fetchAnalyses (subId: string, { reFetch = false }: { reFetch?: boolean } = {}): Promise<PredictPendoAnalysis[]> {
            const cached = this.analysesBySubId[subId];

            if (!reFetch && cached) {
                return Promise.resolve(cached);
            }

            const inFlight = inFlightFetches.get(subId);

            if (inFlight) {
                return inFlight;
            }

            const request = getAvailableAnalyses(subId)
                .then((analyses) => {
                    this.analysesBySubId = { ...this.analysesBySubId, [subId]: analyses };
                    this.failedSubIds = this.failedSubIds.filter((failedSubId) => failedSubId !== subId);

                    return analyses;
                }, (error) => {
                    const analysesBySubId = { ...this.analysesBySubId };

                    delete analysesBySubId[subId];
                    this.analysesBySubId = analysesBySubId;
                    this.failedSubIds = [...new Set([...this.failedSubIds, subId])];
                    throw error;
                })
                .finally(() => {
                    inFlightFetches.delete(subId);
                });

            inFlightFetches.set(subId, request);

            return request;
        },
        async load (): Promise<void> {
            const subId = activeSubId();

            if (!subId || this.isLoaded) {
                return;
            }

            try {
                await this.fetchAnalyses(subId);
            } catch (error) {
                console.error('[predict] failed to load Adoption Agent access', error);
            }
        },
    },
});

if (module.hot) {
    acceptHMRUpdate(usePredictAccessStore, module.hot);
}
